import StoreBadge from './StoreBadge';
import { default as NextImage } from 'next/image';
import { buildModule } from '@/components/Modules';
import { Band } from '@/components/Modules/Band/Band';
import type { AppStoreModuleFragment } from '@/utils/dato/generated';

const AppStoreModule = ({
  googlePlayBadge,
  appStoreBadge,
  awardImage,
  features,
  button,
  title,
  image,
}: AppStoreModuleFragment) => {
  const ctaItem = button[0];
  const appStoreBadgeItem = appStoreBadge[0];
  const googlePlayBadgeItem = googlePlayBadge[0];

  // App Store buttons are shown instead of the CTA button, rating badges are shown in both cases
  const hasStoreButtons = ctaItem?.__typename === 'AppStoreRecord';

  const phoneImage = (
    <NextImage
      className="max-w-[477px] w-full h-auto"
      height={image.height ?? 516}
      width={image.width ?? 477}
      alt={image.alt ?? ''}
      src={image.url}
    />
  );

  return (
    <Band background="#EFFFF6" spacing="medium" contained>
      <div className="flex flex-col md:flex-row md:items-start">
        <div className="md:w-[55%]">
          <h2 className="text-4xl lg:text-5xl font-serif font-semibold mb-7 text-base-content">{title}</h2>

          {features.length > 0 ? (
            <ul className="flex flex-col gap-4 mb-8">
              {features.map((feature) => (
                <li className="flex items-center gap-3" key={feature.id}>
                  <span
                    className="flex items-center justify-center w-6 h-6 rounded-full bg-[#AEE6A1] shrink-0"
                    aria-hidden="true"
                  >
                    <NextImage src="/images/checkmark.svg" width={11} height={8} alt="" />
                  </span>

                  <span className="text-base">{feature.text}</span>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="md:hidden mb-8 flex justify-center">{phoneImage}</div>

          {hasStoreButtons ? (
            <div className="md:mb-8 flex justify-center md:justify-start">{buildModule({ module: ctaItem })}</div>
          ) : null}

          {ctaItem && !hasStoreButtons ? (
            <div className="mb-8 [&>.btn]:w-full md:[&>.btn]:w-auto">{buildModule({ module: ctaItem })}</div>
          ) : null}

          {appStoreBadgeItem || googlePlayBadgeItem || awardImage ? (
            <div className="flex flex-wrap gap-4 items-center justify-center md:justify-start">
              {appStoreBadgeItem ? (
                <StoreBadge
                  icon={<NextImage src="/images/app-store-sm.svg" alt="App Store" height={18} width={18} />}
                  reviewCount={appStoreBadgeItem.reviewCount}
                  rating={appStoreBadgeItem.rating}
                  label={appStoreBadgeItem.label}
                />
              ) : null}

              {googlePlayBadgeItem ? (
                <StoreBadge
                  icon={<NextImage src="/images/google-play-sm.svg" alt="Google Play" height={18} width={18} />}
                  reviewCount={googlePlayBadgeItem.reviewCount}
                  rating={googlePlayBadgeItem.rating}
                  label={googlePlayBadgeItem.label}
                />
              ) : null}

              {awardImage ? (
                <NextImage
                  className="h-auto max-h-12 w-auto"
                  height={awardImage.height ?? 70}
                  width={awardImage.width ?? 260}
                  alt={awardImage.alt ?? ''}
                  src={awardImage.url}
                />
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="hidden md:flex md:w-[45%] justify-center md:-mb-9 lg:-mb-11">{phoneImage}</div>
      </div>
    </Band>
  );
};

export default AppStoreModule;
